<template>
  <v-dialog 
    v-model="dialogRes"
    :fullscreen="$vuetify.display.mdAndDown" 
    transition="dialog-top-transition"
    :scroll-strategy="'none'" 
    class="resDialog">

    <v-card flat class="bookingCard">

      <!--Reihe mit Favoriten-Knopf und Exit-Button-->
      <v-row class="align-center justify-space-between mt-n2 mr-1">
        <v-col cols="auto">
          <FavoriteButton 
            :favBtnIsFavorite="resDialogReservation.isFavorite"
            :favBtnSeatID="resDialogReservation.seat.id"
            :favBtnFavoriteID="resDialogReservation.favoriteID"
            @updateFavorite="$emit('updateFavorite')"
            />
        </v-col>
        <v-col cols="auto">
          <ExitButton @click="dialogRes = false" />
        </v-col>
      </v-row>

      <!--Reihe mit Titel und Subtitel-->    
      <v-row dense class="justify-center align-center mt-n8">
        <v-col cols="auto">
          <v-card-title>
            {{ resDialogReservation.seat.description }}
          </v-card-title>
        </v-col>
      </v-row>
      <v-row dense class="justify-center align-center mt-n4">
        <v-col cols="auto">
            <v-card-subtitle>
              {{ resDialogReservation.seat.room + ', Platz ' + resDialogReservation.seat.name }}
            </v-card-subtitle>
        </v-col>
      </v-row>

      <!--Reihe mit Benutzer-->
      <v-row class="align-center justify-center"> 
        <v-col cols="auto">
          <DropDownEmployees
            v-model="selectedUserID"
            :dropDownEmpUsers="resDialogUsers"
            disabled/>
        </v-col>
      </v-row>

      <v-row class="justify-center align-stretch ma-4">
        <!-- DatePicker -->
        <v-col cols="6">
          <v-card flat class="datePickerCard h-100">
            <v-date-picker
              v-model="reservationDates"
              color="#FF7500"
              locale="de-DE"
              first-day-of-week="1"
              show-adjacent-months
              multiple
              :allowed-dates="isDateAllowed"
            />
          </v-card>
        </v-col>

        <!-- Textarea -->
        <v-col cols="6" class="d-flex flex-column">
          <v-textarea
            v-model="reservationComment"
            label="Kommentar"
            prepend-inner-icon="mdi-message"
            variant="solo"
            clearable
            :auto-grow=false
            class="fill-height commentaryField"
          />
        </v-col>
      </v-row>

      <!--Aktionen-->
      <v-row dense class="justify-end align-center mr-4 mt-n10 mb-2">
        <v-card-actions>
        <v-btn 
          variant="elevated" 
          color="primary"
          prepend-icon="mdi-check-circle" 
          @click="confirmResChange">
          Reservierungsänderung bestätigen
        </v-btn>
        <v-btn 
          variant="elevated" 
          color="grey" 
          @click="dialogRes = false">
          Abbrechen
        </v-btn>
      </v-card-actions>
      </v-row>
    </v-card>
  </v-dialog>

  <SnackbarBooking ref="snackbarBookingRef" />
</template>

<script>
import ExitButton from '../consumables/ExitButton.vue'
import FavoriteButton from '../consumables/FavoriteButton.vue'
import DropDownEmployees from '../consumables/DropDownEmployees.vue'
import SnackbarBooking from '../consumables/SnackbarBooking.vue'

export default {
  name: 'ReservationDialog',

  components: {
    ExitButton,
    FavoriteButton,
    DropDownEmployees,
    SnackbarBooking,
  },

  props: {
    resDialogReservation: {type: Object, default: null},
    resDialogUsers: {type: Object, default: null},
    resDialogStartDate: {type: String, required: true},
    resDialogEndDate: {type: String, required: true}
  },

  emits: ['updateReservation', 'updateFavorite'],

  data() {

    return {
      dialogRes: false,
      bookedDates: [],
      reservationDates: [],
      reservedDates: [],
      releaseDates: [],
      updatedResItems: [],
      selectedUserID: null
    }
  },

  methods: {

    async openResDialog() {
      await this.getBookedDates()
      this.getReservationDates(this.resDialogReservation)
      this.getReservedDates()
      await this.getReleaseDates()
      this.selectedUserID = this.resDialogReservation.reserved_for
      this.reservationComment = this.resDialogReservation.comment
      this.dialogRes = true
    },

    async getBookedDates() {
      if (!this.resDialogReservation.seat.id) return
      try {
        const response = await this.$axios.get(`/api/seats/${this.resDialogReservation.seat.id}/booked-dates/`)
        this.bookedDates = response.data
      } catch (error) {
        console.error("Fehler beim Laden der gebuchten Daten:", error)
      }
    },

    getReservationDates(reservation) {
      if (reservation.reservation_items?.length) {
        let allDates = []
        reservation.reservation_items.forEach(item => {

          const startRes = new Date(item.start)
          const endRes = new Date(item.end)
          const current = new Date(startRes)

          while (current <= endRes) {
            allDates.push(current.toISOString().split('T')[0])
            current.setUTCDate(current.getUTCDate() + 1)
          }
        })
        this.reservationDates = allDates
      } else {
        this.reservationDates = []
      }
    },

    getReservedDates() {
      this.reservedDates = this.bookedDates.filter(date => !this.reservationDates.includes(date))
    },

    async getReleaseDates() {
      this.releaseDates = []

      if (!this.resDialogReservation.seat.id) return
      if (!this.resDialogReservation.seat.owner) return

      try {
        const response = await this.$axios.get(`/api/owner-release-items/`, {
          params: {seat: this.resDialogReservation.seat.id}})
        const dataArray = response.data
        this.releaseDates = dataArray.map(item => item.date)

      }catch(err){
        console.error('Fehler beim Laden der Freigaben im Buchungsvorgang', err)
      }
    },

    isDateAllowed(date) {
      const formattedDate = this.formatDateToYMD(date)
      const today = this.formatDateToYMD(new Date())
      const isOwnerSeat = !!this.resDialogReservation?.seat?.owner

      // 1️⃣ bestehende Reservierung IMMER erlauben
      if (this.reservationDates.includes(formattedDate)) {
        return true
      }

      // 2️⃣ Wochenenden sperren
      const day = new Date(date).getDay()
      if (day === 0 || day === 6) return false

      // 3️⃣ Maximal 3 Monate
      const maxDate = new Date()
      maxDate.setMonth(maxDate.getMonth() + 3)
      if (new Date(date) > maxDate) return false

      // 4️⃣ OwnerSeat-Regel
      if (isOwnerSeat) {
        return (
          this.releaseDates.includes(formattedDate) &&
          !this.reservedDates.includes(formattedDate) &&
          formattedDate >= today
        )
      }

      // 5️⃣ Normaler Sitz
      return (
        !this.reservedDates.includes(formattedDate) &&
        formattedDate >= today
      )
    },

    async confirmResChange() {

      const isOwnerSeat = !!this.resDialogReservation?.seat?.owner
      if (isOwnerSeat) {
        const invalidDates = this.reservationDates.filter(
          d => !this.releaseDates.includes(d)
        )

        if (invalidDates.length > 0) {
          console.error('Ungültige Tage für OwnerSeat:', invalidDates)
          return
        }
      }

      this.reservationDates.forEach(date => { 
        const formattedDate = this.formatDateToYMD(date)
        const resItem = {start: formattedDate, end: formattedDate}
        this.updatedResItems.push(resItem)
      })      
      console.log('Formatierte Daten aus DatePicker: ', this.updatedResItems)

      try{
        await this.$axios.patch(`/api/reservations/${this.resDialogReservation.id}/`, {
          comment: this.reservationComment,
          reservation_items: this.updatedResItems
        })
        this.$emit('updateReservation')
        this.updatedResItems = []
        this.dialogRes = false
      } catch{
          console.error('Fehler beim Patchen einer Reservierung:', err)
          this.updatedResItems = []
          this.dialogRes = false
        }
    },

    formatDateToYMD(date) {
      if (typeof date === 'string') return date
        const newDate = new Date(date)
        const year = newDate.getFullYear()
        const month = String(newDate.getMonth() + 1).padStart(2, '0')
        const day = String(newDate.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
  }
}
</script>

<style scoped>
.resDialog {
  max-width: 49rem;
}
.bookingCard {
  scrollbar-width: none !important;
  -ms-overflow-style: none;
}
.datePickerCard {
  height: 29rem !important;
  box-shadow: 0px 6px 15px hsla(0, 0%, 0%, 0.5);
}
.commentaryField {
  height: 30.5rem !important;
  flex-direction: column;
}
@media (max-width: 600px) {
  .btn-responsive{
    font-size: 0.5rem;
  }
}
</style>
